import { subDays, startOfDay, endOfDay } from 'date-fns';
import type { PartnerRuleCriterion } from '@shipium-corp/shipium-ts-types';
import { TYPESENSE_FIELDS, DEFAULT_VALUES } from '~/utils/shipmentSearch/typesense-constants';
import { LEGACY_HAZMAT_ALIASES } from '../shipmentSearch/hazmat-classification-utils';

export interface RuleImpactFilterResult {
  filterBy: string;
  carrierMethodFilter?: string;
  carrierAndServiceMethodList?: string[];
  hasUnsupportedCriteria: boolean;
}

const WEIGHT_CONVERSION_TO_LBS: Record<string, number> = {
  lb: 1,
  oz: 1 / 16,
  kg: 2.20462,
  g: 0.00220462,
};

const COMPARATOR_TO_OPERATOR: Record<string, string> = {
  in: ':=',
  not_in: ':!=',
  gte: ':>=',
  gt: ':>',
  lte: ':<=',
  lt: ':<',
  eq: ':=',
};

const PROPERTY_TO_FIELD: Record<string, string> = {
  origin: TYPESENSE_FIELDS.ORIGIN_ID_FACET,
  origin_country: TYPESENSE_FIELDS.ORIGIN_COUNTRY_CODE_FACET,
  origin_postal: TYPESENSE_FIELDS.ORIGIN_POSTAL_CODE,
  destination_country: TYPESENSE_FIELDS.DESTINATION_COUNTRY_CODE_FACET,
  destination_postal: TYPESENSE_FIELDS.DESTINATION_POSTAL_CODE,
  weight: TYPESENSE_FIELDS.WEIGHT_POUNDS,
  ship_option: TYPESENSE_FIELDS.SHIP_OPTION_FACET,
  shipment_tag: TYPESENSE_FIELDS.SHIPMENT_TAGS,
  hazmat: TYPESENSE_FIELDS.SHIPMENT_CLASSIFICATIONS_FACET,
  full_hazmat: TYPESENSE_FIELDS.SHIPMENT_CLASSIFICATIONS_FACET,
  limited_quantity: TYPESENSE_FIELDS.SHIPMENT_CLASSIFICATIONS_FACET,
  origin_region: TYPESENSE_FIELDS.ORIGIN_STATE_FACET,
  destination_region: TYPESENSE_FIELDS.DESTINATION_STATE_FACET,
  po_box: TYPESENSE_FIELDS.SHIPMENT_PROPERTIES_FACET,
  po_box_plus_hc_and_rr: TYPESENSE_FIELDS.SHIPMENT_PROPERTIES_FACET,
  exact_date_delivery: TYPESENSE_FIELDS.SHIPMENT_PROPERTIES_FACET,
};

const PROPERTY_VALUES: Record<string, string[]> = {
  po_box: ['po_box'],
  po_box_plus_hc_and_rr: ['po_box', 'hc_box', 'rr_box'],
  exact_date_delivery: ['exact_date_delivery'],
  hazmat: ['hazmat'],
  full_hazmat: ['full_hazmat', LEGACY_HAZMAT_ALIASES.full_hazmat],
  limited_quantity: ['limited_quantity_hazmat', LEGACY_HAZMAT_ALIASES.limited_quantity_hazmat],
};

const includeOrExclude = (isInclude: boolean): string =>
  isInclude ? COMPARATOR_TO_OPERATOR.in : COMPARATOR_TO_OPERATOR.not_in;

export function buildBaseFilter(): string {
  const now = new Date();
  const start = startOfDay(subDays(now, 7)).getTime();
  const end = endOfDay(now).getTime();
  return [
    `${TYPESENSE_FIELDS.CREATED_DATE_TIME}:[${start}..${end}]`,
    `${TYPESENSE_FIELDS.CARRIER_SELECTION_STATUS_FACET}:=success`,
    `${TYPESENSE_FIELDS.TEST_MODE_FACET}:=false`,
  ].join(DEFAULT_VALUES.FILTER_OPERATOR);
}

/**
 * Build a Typesense filter clause for the carrier/service method list from a rule.
 * The list is OR'd across both carrier_id_facet and carrier_service_method_id_facet —
 * Typesense simply won't match values that don't appear in a given facet's value set,
 * so we don't need to pre-split the list by ID shape.
 */
export function buildCarrierMethodFilter(carrierAndServiceMethodList: string[]): string | undefined {
  if (!carrierAndServiceMethodList.length) {
    return undefined;
  }

  const formatted = carrierAndServiceMethodList.join(',');
  return `(${TYPESENSE_FIELDS.CARRIER_ID_FACET}:=[${formatted}] || ${TYPESENSE_FIELDS.CARRIER_SERVICE_METHOD_ID_FACET}:=[${formatted}])`;
}

function formatArrayFilter(field: string, operator: string, values: unknown[]): string {
  const formatted = values.map((v) => String(v));
  return `${field}${operator}[${formatted.join(',')}]`;
}

/**
 * Convert saved rule criteria (from the list page) to a Typesense filter_by string.
 */
export function ruleCriteriaToFilter(
  criteria: PartnerRuleCriterion[],
  tenantIds?: string[],
  carrierAndServiceMethodList?: string[],
): RuleImpactFilterResult {
  const filters: string[] = [buildBaseFilter()];
  let hasUnsupportedCriteria = false;

  if (tenantIds?.length) {
    filters.push(formatArrayFilter(TYPESENSE_FIELDS.TENANT_ID_FACET, COMPARATOR_TO_OPERATOR.in, tenantIds));
  }

  for (const criterion of criteria) {
    const propertyEnum = criterion.ruleProperty?.rulePropertyEnum;
    const comparatorEnum = criterion.ruleComparator?.ruleComparatorEnum;
    const value = criterion.compareValue as unknown;

    if (!propertyEnum) {
      continue;
    }

    const field = PROPERTY_TO_FIELD[propertyEnum];
    if (!field) {
      hasUnsupportedCriteria = true;
      continue;
    }

    // Multi-value flag properties (PO Box / HC / RR / EDD, hazmat). Each property has a fixed
    // list of facet values to query; compareValue true = include, false = exclude.
    if (PROPERTY_VALUES[propertyEnum]) {
      const operator = includeOrExclude(value === true);
      filters.push(formatArrayFilter(field, operator, PROPERTY_VALUES[propertyEnum]));
      continue;
    }

    // Handle weight criteria (scalar with unit conversion)
    if (propertyEnum === 'weight') {
      const unit = criterion.scalarUnit?.scalarUnitEnum || 'lb';
      const conversionFactor = WEIGHT_CONVERSION_TO_LBS[String(unit)] ?? 1;
      const weightInLbs = Number(value) * conversionFactor;
      const operator = comparatorEnum ? COMPARATOR_TO_OPERATOR[comparatorEnum] : undefined;
      if (operator) {
        filters.push(`${field}${operator}${weightInLbs}`);
      }
      continue;
    }

    // Handle array values (in/not_in)
    if (Array.isArray(value)) {
      const operator = includeOrExclude(comparatorEnum !== 'not_in');
      filters.push(formatArrayFilter(field, operator, value));
      continue;
    }

    // Handle scalar comparators
    const operator = comparatorEnum ? COMPARATOR_TO_OPERATOR[comparatorEnum] : undefined;
    if (operator) {
      filters.push(`${field}${operator}${String(value)}`);
    }
  }

  const carrierMethodFilter = carrierAndServiceMethodList?.length
    ? buildCarrierMethodFilter(carrierAndServiceMethodList)
    : undefined;

  return {
    filterBy: filters.join(DEFAULT_VALUES.FILTER_OPERATOR),
    carrierMethodFilter,
    carrierAndServiceMethodList,
    hasUnsupportedCriteria,
  };
}
